import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";

// Global state: only ONE cart icon should be the primary processor
/** @type {CartIcon | null} */
let primaryCartIcon = null;

// Track the last processed event to prevent double-processing
/** @type {number | null} */
let lastProcessedEventId = null;

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ["cartBubble", "cartBubbleText", "cartBubbleCount"];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? "0", 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : "";
  }

  connectedCallback() {
    super.connectedCallback();

    // First cart icon to connect becomes the primary
    if (!primaryCartIcon) {
      primaryCartIcon = this;
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // If primary cart is being removed, clear it
    if (primaryCartIcon === this) {
      primaryCartIcon = null;
    }

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === "product-form-component";
    const eventId = event.timeStamp;

    // Only the primary cart icon processes increments
    // All other cart icons just sync from sessionStorage
    if (this === primaryCartIcon) {
      // Check if this exact event was already processed
      if (lastProcessedEventId === eventId) {
        return; // Skip duplicate processing
      }
      lastProcessedEventId = eventId;

      // Primary cart - process normally and save to sessionStorage
      this.renderCartBubble(itemCount, comingFromProductForm);
    } else {
      // Secondary cart - just read from sessionStorage after a brief delay
      setTimeout(() => {
        this.ensureCartBubbleIsCorrect();
      }, 100);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart (delta when from product form, total otherwise).
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Calculate the new count
    let newCount;
    if (comingFromProductForm) {
      // When adding from product form, itemCount is the delta to add
      // But we need to get the real count from sessionStorage first
      const storedData = sessionStorage.getItem("cart-count");
      let currentStoredCount = 0;
      if (storedData) {
        try {
          const { value } = JSON.parse(storedData);
          currentStoredCount = parseInt(value, 10) || 0;
        } catch (_) {
          currentStoredCount = 0;
        }
      }
      newCount = currentStoredCount + itemCount;
    } else {
      // When not from product form, itemCount is the total
      newCount = itemCount;
    }

    // Always show the bubble and count, even when count is 0
    this.refs.cartBubbleCount.classList.remove("hidden");
    this.refs.cartBubble.classList.remove("visually-hidden");
    this.refs.cartBubble.classList.toggle("cart-bubble--animating", newCount > 0 && animate);

    // Always set the count, including 0
    this.currentCartCount = newCount;

    this.classList.toggle("header-actions__cart-icon--has-cart", newCount > 0);

    // Save to sessionStorage
    sessionStorage.setItem(
      "cart-count",
      JSON.stringify({
        value: String(newCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove("cart-bubble--animating");
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem("cart-count");
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) {
      // If no sessionStorage, ensure bubble is visible with current count (including 0)
      const currentCount = parseInt(visibleCount || "0", 10);
      this.refs.cartBubbleCount.classList.remove("hidden");
      this.refs.cartBubble.classList.remove("visually-hidden");
      this.currentCartCount = currentCount;
      return;
    }

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        // Always render, including when count is 0
        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // If parsing fails, ensure bubble is visible with current count
      const currentCount = parseInt(visibleCount || "0", 10);
      this.refs.cartBubbleCount.classList.remove("hidden");
      this.refs.cartBubble.classList.remove("visually-hidden");
      this.currentCartCount = currentCount;
    }
  };
}

if (!customElements.get("cart-icon")) {
  customElements.define("cart-icon", CartIcon);
}
